/**
 * TodoDB.ts
 *
 * IndexedDB 기반 Todo 데이터 관리 시스템
 * TodoDB.md의 설계를 기반으로 구현
 */

import settings from 'popups/Settings'
import { TodoFolder } from '../TodoFolder'
import { TodoItem, todoItemForDB, todoItemFromDB } from '../TodoItem'

export interface TodoDBConfig {
  name: string
  version: number
  stores: {
    todos: string
    folders: string
    searchIndex?: string
  }
}

const DB_CONFIG: TodoDBConfig = {
  name: 'TodoDB', // DB 이름
  version: 1, // DB 버전
  stores: {
    todos: 'todos', // todo table
    folders: 'folders', // folder table
  },
}

export class TodoDB {
  private dbName: string
  private version: number
  private db: IDBDatabase | null
  private isActive: boolean
  private isInitializing: boolean
  private initWaitingRequests: Array<{
    resolve: (value: boolean) => void
    reject: (reason: any) => void
  }>

  constructor() {
    this.dbName = DB_CONFIG.name
    this.version = DB_CONFIG.version
    this.db = null
    this.isActive = false
    this.isInitializing = false
    this.initWaitingRequests = []
  }

  /**
   * 데이터베이스 초기화
   */
  async init(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (this.isInitializing) {
        this.initWaitingRequests.push({ resolve, reject })
        return
      }

      this.isInitializing = true
      const request = indexedDB.open(this.dbName, this.version)

      request.onerror = () => {
        this.isActive = false
        this.isInitializing = false
        this.rejectWaitingRequests(request.error)
        reject(request.error)
      }

      request.onsuccess = () => {
        this.db = request.result
        this.isActive = true
        this.isInitializing = false
        this.resolveWaitingRequests()
        console.log('TodoDB initialized successfully')
        resolve(true)
      }

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result
        this.setupSchema(db)
      }
    })
  }

  /**
   * 데이터베이스 스키마 설정
   */
  private setupSchema(db: IDBDatabase): void {
    console.log('Setting up TodoDB schema...')

    // todos Object Store
    if (!db.objectStoreNames.contains(DB_CONFIG.stores.todos)) {
      const todosStore = db.createObjectStore(DB_CONFIG.stores.todos, { keyPath: 'url' })

      // 실제 사용되는 단일 인덱스만 유지
      todosStore.createIndex('folderUrl', 'folderUrl', { unique: false })
      todosStore.createIndex('status', 'status', { unique: false })
      todosStore.createIndex('priority', 'priority', { unique: false })

      console.log('todos store created with essential indexes')
    }

    // folders Object Store
    if (!db.objectStoreNames.contains(DB_CONFIG.stores.folders)) {
      const foldersStore = db.createObjectStore(DB_CONFIG.stores.folders, { keyPath: 'url' })

      console.log('folders store created', foldersStore)
    }
  }

  /**
   * 대기 중인 요청들 해결
   */
  private resolveWaitingRequests(): void {
    this.initWaitingRequests.forEach((request) => {
      request.resolve(true)
    })
    this.initWaitingRequests = []
  }

  /**
   * 대기 중인 요청들 거부
   */
  private rejectWaitingRequests(error: any): void {
    this.initWaitingRequests.forEach((request) => {
      request.reject(error)
    })
    this.initWaitingRequests = []
  }

  /**
   * 데이터베이스 연결 종료
   */
  close(): void {
    if (this.isActive && this.db) {
      this.db.close()
    }
    this.isActive = false
    this.db = null
    console.log('TodoDB connection closed')
  }

  /**
   * 데이터베이스 활성 상태 확인
   */
  isReady(): boolean {
    return this.isActive && this.db !== null
  }

  /**
   * 데이터베이스가 준비될 때까지 대기
   */
  async ensureReady(): Promise<void> {
    if (!this.isReady()) {
      await this.init()
    }
  }

  /**
   * 트랜잭션 완료 대기 헬퍼
   */
  protected waitForTransaction(transaction: IDBTransaction): Promise<void> {
    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve()
      transaction.onerror = () => reject(transaction.error)
      transaction.onabort = () => reject(new Error('Transaction aborted'))
    })
  }

  /**
   * Store에서 Promise 기반으로 데이터 조작
   */
  protected putToStore(store: IDBObjectStore, data: any): Promise<any> {
    return new Promise((resolve, reject) => {
      const request = store.put(data)
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  protected getFromStore(store: IDBObjectStore, key: any): Promise<any> {
    return new Promise((resolve, reject) => {
      const request = store.get(key)
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  protected deleteFromStore(store: IDBObjectStore, key: any): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = store.delete(key)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  protected getAllFromStore(store: IDBObjectStore): Promise<any[]> {
    return new Promise((resolve, reject) => {
      const request = store.getAll()
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  protected getAllFromIndex(index: IDBIndex, query?: IDBValidKey | IDBKeyRange): Promise<any[]> {
    return new Promise((resolve, reject) => {
      const request = query ? index.getAll(query) : index.getAll()
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  /**
   * =====================
   * 2단계: 기본 CRUD 작업
   * =====================
   */

  /**
   * Todo 항목 저장/업데이트
   */
  async saveTodo(todo: TodoItem): Promise<void> {
    await this.ensureReady()

    const transaction = this.db!.transaction([DB_CONFIG.stores.todos], 'readwrite')
    const store = transaction.objectStore(DB_CONFIG.stores.todos)

    // RRule 객체를 문자열로 변환하여 저장
    const todoForDB = todoItemForDB(todo)

    await this.putToStore(store, todoForDB)
    await this.waitForTransaction(transaction)
  }

  /**
   * Todo 항목 조회
   */
  async getTodo(url: string): Promise<TodoItem | null> {
    await this.ensureReady()

    const transaction = this.db!.transaction([DB_CONFIG.stores.todos], 'readonly')
    const store = transaction.objectStore(DB_CONFIG.stores.todos)

    const result = await this.getFromStore(store, url)
    if (!result) return null

    // DB에서 가져온 데이터를 TodoItem으로 변환 (문자열 → RRule)
    return todoItemFromDB(result)
  }

  /**
   * Todo 항목 삭제
   */
  async deleteTodo(url: string): Promise<void> {
    await this.ensureReady()

    const transaction = this.db!.transaction([DB_CONFIG.stores.todos], 'readwrite')

    // Todo 삭제
    const todosStore = transaction.objectStore(DB_CONFIG.stores.todos)
    await this.deleteFromStore(todosStore, url)

    await this.waitForTransaction(transaction)
  }

  /**
   * 모든 Todo 항목 조회
   */
  async getAllTodos(): Promise<TodoItem[]> {
    await this.ensureReady()

    const transaction = this.db!.transaction([DB_CONFIG.stores.todos], 'readonly')
    const store = transaction.objectStore(DB_CONFIG.stores.todos)

    return await this.getAllFromStore(store)
  }

  /**
   * 폴더별 Todo 항목 조회
   */
  async getTodosByFolder(folderUrl: string): Promise<TodoItem[]> {
    await this.ensureReady()

    const transaction = this.db!.transaction([DB_CONFIG.stores.todos], 'readonly')
    const store = transaction.objectStore(DB_CONFIG.stores.todos)
    const index = store.index('folderUrl')

    const results = await this.getAllFromIndex(index, folderUrl)

    // DB에서 가져온 데이터들을 TodoItem으로 변환
    return results.map(todoItemFromDB)
  }

  /**
   * 여러 Todo 항목 일괄 저장
   */
  async saveTodos(todos: TodoItem[]): Promise<void> {
    await this.ensureReady()

    const transaction = this.db!.transaction([DB_CONFIG.stores.todos], 'readwrite')
    const todosStore = transaction.objectStore(DB_CONFIG.stores.todos)

    // RRule 객체를 문자열로 변환하여 저장
    for (const todo of todos) {
      const todoForDB = todoItemForDB(todo)
      await this.putToStore(todosStore, todoForDB)
    }

    await this.waitForTransaction(transaction)
  }

  /**
   * 폴더의 모든 Todo를 트랜잭션 사용으로 교체 방법 - 좀더 테스트를 해보고 사용하기로.
   */
  async replaceAllTodosInFolder(folderUrl: string, newTodos: TodoItem[]): Promise<void> {
    await this.ensureReady()

    // 단일 트랜잭션에서 삭제 + 추가 작업 수행
    const transaction = this.db!.transaction([DB_CONFIG.stores.todos], 'readwrite')
    const store = transaction.objectStore(DB_CONFIG.stores.todos)
    const index = store.index('folderUrl')

    try {
      // 1단계: 해당 폴더의 기존 todos 모두 조회 후 삭제.
      const existingTodosRequest = index.getAll(folderUrl)
      const existingTodos = await new Promise<any[]>((resolve, reject) => {
        existingTodosRequest.onsuccess = () => resolve(existingTodosRequest.result)
        existingTodosRequest.onerror = () => reject(existingTodosRequest.error)
      })

      // 기존 todos 삭제
      for (const existingTodo of existingTodos) {
        await this.deleteFromStore(store, existingTodo.url)
      }

      // 2단계: 새로운 todos 저장
      for (const todo of newTodos) {
        const todoForDB = todoItemForDB(todo)
        await this.putToStore(store, todoForDB)
      }

      // 트랜잭션 완료 대기
      await this.waitForTransaction(transaction)

      console.log(
        `[replaceAllTodosInFolder] Replaced ${existingTodos.length} → ${newTodos.length} todos in folder: ${folderUrl}`,
      )
    } catch (error) {
      console.error(`[replaceAllTodosInFolder] Transaction failed for folder ${folderUrl}:`, error)
      throw error
    }
  }

  /**
   * 폴더 저장/업데이트
   */
  async saveFolder(folder: TodoFolder): Promise<void> {
    await this.ensureReady()

    const transaction = this.db!.transaction([DB_CONFIG.stores.folders], 'readwrite')
    const store = transaction.objectStore(DB_CONFIG.stores.folders)

    await this.putToStore(store, folder)
    await this.waitForTransaction(transaction)
  }

  /**
   * 폴더 조회
   */
  async getFolder(url: string): Promise<TodoFolder | null> {
    await this.ensureReady()

    const transaction = this.db!.transaction([DB_CONFIG.stores.folders], 'readonly')
    const store = transaction.objectStore(DB_CONFIG.stores.folders)

    const result = await this.getFromStore(store, url)
    return result || null
  }

  /**
   * 모든 폴더 조회
   */
  async getAllFolders(): Promise<TodoFolder[]> {
    await this.ensureReady()

    const transaction = this.db!.transaction([DB_CONFIG.stores.folders], 'readonly')
    const store = transaction.objectStore(DB_CONFIG.stores.folders)

    return await this.getAllFromStore(store)
  }

  /**
   * 폴더 삭제
   */
  async deleteFolder(url: string): Promise<void> {
    await this.ensureReady()

    const transaction = this.db!.transaction([DB_CONFIG.stores.folders], 'readwrite')
    const store = transaction.objectStore(DB_CONFIG.stores.folders)

    await this.deleteFromStore(store, url)
    await this.waitForTransaction(transaction)
  }

  /**
   * 모든 Todo 데이터 삭제 (DB 초기화)
   */
  async clearAllTodos(): Promise<void> {
    await this.ensureReady()

    const transaction = this.db!.transaction([DB_CONFIG.stores.todos], 'readwrite')
    const todosStore = transaction.objectStore(DB_CONFIG.stores.todos)

    // ObjectStore의 모든 데이터 삭제
    await new Promise<void>((resolve, reject) => {
      const request = todosStore.clear()
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })

    await this.waitForTransaction(transaction)
    console.log('All todos cleared from DB')
  }

  /**
   * 모든 폴더 데이터 삭제
   */
  async clearAllFolders(): Promise<void> {
    await this.ensureReady()

    const transaction = this.db!.transaction([DB_CONFIG.stores.folders], 'readwrite')
    const foldersStore = transaction.objectStore(DB_CONFIG.stores.folders)

    // ObjectStore의 모든 데이터 삭제
    await new Promise<void>((resolve, reject) => {
      const request = foldersStore.clear()
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })

    await this.waitForTransaction(transaction)
    console.log('All folders cleared from DB')
  }

  /**
   * 모든 폴더를 새 목록으로 교체 (서버 동기화용)
   */
  async replaceAllFolders(newFolders: TodoFolder[]): Promise<void> {
    await this.ensureReady()

    // folders, todos를 동시에 다루기 위해 단일 트랜잭션 사용
    const transaction = this.db!.transaction(
      [DB_CONFIG.stores.folders, DB_CONFIG.stores.todos],
      'readwrite',
    )
    const foldersStore = transaction.objectStore(DB_CONFIG.stores.folders)
    const todosStore = transaction.objectStore(DB_CONFIG.stores.todos)

    try {
      // 1단계: 기존 폴더 목록과 새 폴더 목록 비교하여 삭제된 폴더 URL 계산
      const existingFolders = await this.getAllFromStore(foldersStore)
      const newFolderUrls = new Set(newFolders.map((f) => f.url))
      const deletedFolderUrls = existingFolders
        .filter((f) => !newFolderUrls.has(f.url))
        .map((f) => f.url)

      // 2단계: 삭제된 폴더들의 todos 정리
      if (deletedFolderUrls.length > 0) {
        const folderUrlIndex = todosStore.index('folderUrl')
        for (const deletedUrl of deletedFolderUrls) {
          const folderTodos = await this.getAllFromIndex(folderUrlIndex, deletedUrl)
          for (const todo of folderTodos) {
            await this.deleteFromStore(todosStore, todo.url)
          }
          console.log(
            `[replaceAllFolders] Cleaned ${folderTodos.length} todos from deleted folder: ${deletedUrl}`,
          )
        }
      }

      // 3단계: 기존 폴더들 삭제
      for (const existingFolder of existingFolders) {
        await this.deleteFromStore(foldersStore, existingFolder.url)
      }

      // 4단계: 새로운 폴더들 저장
      for (const folder of newFolders) {
        await this.putToStore(foldersStore, folder)
      }

      await this.waitForTransaction(transaction)

      console.log(
        `[replaceAllFolders] Replaced ${existingFolders.length} → ${newFolders.length} folders; deletedFolders=${deletedFolderUrls.length}`,
      )
    } catch (error) {
      console.error('[replaceAllFolders] Transaction failed:', error)
      throw error
    }
  }

  /**
   * 전체 DB 데이터 삭제 (모든 스토어 초기화)
   */
  async clearAllData(): Promise<void> {
    await this.ensureReady()

    const transaction = this.db!.transaction(
      [DB_CONFIG.stores.todos, DB_CONFIG.stores.folders],
      'readwrite',
    )
    const todosStore = transaction.objectStore(DB_CONFIG.stores.todos)
    const foldersStore = transaction.objectStore(DB_CONFIG.stores.folders)

    // 모든 스토어 데이터 삭제
    await Promise.all([
      new Promise<void>((resolve, reject) => {
        const request = todosStore.clear()
        request.onsuccess = () => resolve()
        request.onerror = () => reject(request.error)
      }),
      new Promise<void>((resolve, reject) => {
        const request = foldersStore.clear()
        request.onsuccess = () => resolve()
        request.onerror = () => reject(request.error)
      }),
    ])

    await this.waitForTransaction(transaction)
    console.log('All DB data cleared')
  }

  /**
   * ================================
   * 3단계: 인덱스 기반 쿼리 메서드
   * ================================
   */

  /**
   * 상태별 Todo 조회
   */
  async getTodosByStatus(status: string): Promise<TodoItem[]> {
    await this.ensureReady()

    const transaction = this.db!.transaction([DB_CONFIG.stores.todos], 'readonly')
    const store = transaction.objectStore(DB_CONFIG.stores.todos)
    const index = store.index('status')

    return await this.getAllFromIndex(index, status)
  }

  /**
   * 우선순위별 Todo 조회
   */
  async getTodosByPriority(priority: string): Promise<TodoItem[]> {
    await this.ensureReady()

    const transaction = this.db!.transaction([DB_CONFIG.stores.todos], 'readonly')
    const store = transaction.objectStore(DB_CONFIG.stores.todos)
    const index = store.index('priority')

    return await this.getAllFromIndex(index, priority)
  }

  /**
   * 동기화 폴더의 Todo 조회
   *  동기화 폴더의 목록은 settings
   */
  async getSyncFolderTodos(): Promise<TodoItem[]> {
    await this.ensureReady()

    // settings에서 직접 동기화 폴더 목록 가져오기

    const todoSettings = settings.getValue('todo') || {}
    const syncFolders = Array.isArray(todoSettings.todoSyncTargets)
      ? todoSettings.todoSyncTargets
      : []

    if (syncFolders.length === 0) {
      return []
    }

    console.log('[getSyncFolderTodos] DB will query sync folders:', syncFolders)

    const transaction = this.db!.transaction([DB_CONFIG.stores.todos], 'readonly')
    const store = transaction.objectStore(DB_CONFIG.stores.todos)
    const index = store.index('folderUrl')

    const allResults: any[] = []

    for (const folderUrl of syncFolders) {
      const folderTodos = await this.getAllFromIndex(index, folderUrl)
      allResults.push(...folderTodos)
    }

    // DB에서 가져온 데이터들을 TodoItem으로 변환
    return allResults.map(todoItemFromDB)
  }

  /**
   * 페이지네이션을 지원하는 Todo 조회
   */

  /**
   * 폴더 관련 고급 쿼리
   */
  async getSyncFolders(): Promise<TodoFolder[]> {
    await this.ensureReady()

    const allFolders = await this.getAllFolders()
    return allFolders.filter((folder) => folder.isSync === true)
  }

  /**
   * 기본 폴더 조회
   */
  async getDefaultFolder(): Promise<TodoFolder | null> {
    await this.ensureReady()

    const allFolders = await this.getAllFolders()
    return allFolders.find((folder) => folder.isDefault === true) || null
  }

  /**
   * 정렬된 폴더 조회 (TodoFolderSync.getSortedFolders와 동일한 로직)
   */
  async getFoldersSorted(): Promise<TodoFolder[]> {
    await this.ensureReady()

    const allFolders = await this.getAllFolders()
    return allFolders.sort((a, b) => {
      // isDefault가 true인 폴더가 먼저
      if (a.isDefault && !b.isDefault) return -1
      if (!a.isDefault && b.isDefault) return 1

      // isShared가 false인 폴더가 먼저 (개인 폴더 우선)
      if (!a.isShared && b.isShared) return -1
      if (a.isShared && !b.isShared) return 1

      // 같은 그룹 내에서 displayName 오름차순
      return (a.displayName || '').localeCompare(b.displayName || '')
    })
  }

  /**
   * ================================
   * 4단계: 필터링 및 검색 기능
   * ================================
   */

  /**
   * FilterType에 따른 Todo 조회 (TodoFilter.ts 호환)
   */
  async getFilteredTodos(
    filterType: string,
    folderUrls?: string[],
    focusDate: Date | null = null,
  ): Promise<TodoItem[]> {
    await this.ensureReady()

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const now = new Date()

    // 동기화 폴더의 모든 Todo 조회
    let allTodos: TodoItem[]
    if (folderUrls && folderUrls.length > 0) {
      allTodos = []
      for (const folderUrl of folderUrls) {
        const folderTodos = await this.getTodosByFolder(folderUrl)
        allTodos.push(...folderTodos)
      }
    } else {
      allTodos = await this.getSyncFolderTodos()
    }

    switch (filterType) {
      case 'all':
        return allTodos

      case 'today':
        return allTodos.filter((todo) => {
          // Include completed todos in list (count에서만 제외)

          // dueDate가 오늘인지 확인
          if (todo.dueDate) {
            const dueDate = new Date(todo.dueDate)
            dueDate.setHours(0, 0, 0, 0)
            if (dueDate.getTime() === today.getTime()) return true
          }

          return false
        })

      case 'focusDate':
        const fDate = new Date(focusDate) || today
        fDate.setHours(0, 0, 0, 0)
        return allTodos.filter((todo) => {
          if (todo.dueDate) {
            const dueDate = new Date(todo.dueDate)
            dueDate.setHours(0, 0, 0, 0)
            return dueDate.getTime() === fDate.getTime()
          }
          return false
        })

      case 'important':
        return allTodos.filter((todo) => todo.status !== 'COMPLETED' && todo.priority === 'HIGH')

      case 'planned':
        return allTodos.filter(
          (todo) => todo.status !== 'COMPLETED' && (todo.startDate || todo.dueDate),
        )

      case 'overdue':
        return allTodos.filter((todo) => {
          if (todo.status === 'COMPLETED' || !todo.dueDate) return false
          const dueDate = new Date(todo.dueDate)
          dueDate.setHours(0, 0, 0, 0) // Set to start of day for comparison
          const nowDay = new Date(now)
          nowDay.setHours(0, 0, 0, 0) // Set to start of day for comparison
          return dueDate < nowDay
        })

      case 'completed':
        return allTodos.filter((todo) => todo.status === 'COMPLETED')

      default:
        return []
    }
  }

  async getTodosWithRange(
    startDate: Date,
    endDate: Date,
    folderUrls?: string[],
  ): Promise<TodoItem[]> {
    if (!startDate || !endDate) {
      return []
    }

    await this.ensureReady()

    if (!folderUrls || folderUrls.length === 0) {
      const todoSettings = settings.getValue('todo') || {}
      folderUrls = Array.isArray(todoSettings.todoSyncTargets) ? todoSettings.todoSyncTargets : []
    }

    const allTodos: TodoItem[] = []
    for (const folderUrl of folderUrls) {
      const folderTodos = await this.getTodosByFolder(folderUrl)
      folderTodos.forEach((todo) => {
        if (todo.dueDate && todo.dueDate >= startDate && todo.dueDate < endDate) {
          allTodos.push(todo)
        }
      })
    }

    return allTodos
  }

  /**
   * FilterType별 개수 계산 (TodoFilter.ts 호환)
   */
  async getFilterCounts(folderUrls?: string[]): Promise<Record<string, number>> {
    await this.ensureReady()

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const now = new Date()

    // 동기화 폴더의 모든 Todo 조회
    let allTodos: TodoItem[]
    if (folderUrls && folderUrls.length > 0) {
      allTodos = []
      for (const folderUrl of folderUrls) {
        const folderTodos = await this.getTodosByFolder(folderUrl)
        allTodos.push(...folderTodos)
      }
    } else {
      allTodos = await this.getSyncFolderTodos()
    }

    const counts = {
      all: 0,
      today: 0,
      important: 0,
      planned: 0,
      overdue: 0,
      completed: 0,
    }

    // 한 번의 순회로 모든 카운트 계산
    allTodos.forEach((todo) => {
      const isCompleted = todo.status === 'COMPLETED'
      const isImportant = todo.priority === 'HIGH'
      const hasPlannedDate = !!(todo.startDate || todo.dueDate)

      // Today 체크
      let isToday = false
      if (todo.dueDate) {
        const dueDate = new Date(todo.dueDate)
        dueDate.setHours(0, 0, 0, 0)
        isToday = dueDate.getTime() === today.getTime()
      }
      if (!isToday && todo.startDate) {
        const startDate = new Date(todo.startDate)
        startDate.setHours(0, 0, 0, 0)
        isToday = startDate.getTime() === today.getTime()
      }

      // Overdue 체크
      let isOverdue = false
      if (!isCompleted && todo.dueDate) {
        const dueDate = new Date(todo.dueDate)
        dueDate.setHours(0, 0, 0, 0) // Set to start of day for comparison
        const nowDay = new Date(now)
        nowDay.setHours(0, 0, 0, 0) // Set to start of day for comparison
        isOverdue = dueDate < nowDay
      }

      // 카운트 증가
      if (isCompleted) {
        counts.completed++
      } else {
        counts.all++
        if (isToday) counts.today++
        if (isImportant) counts.important++
        if (hasPlannedDate) counts.planned++
        if (isOverdue) counts.overdue++
      }
    })

    return counts
  }

  /**
   * 오늘의 Todo (활성 + 완료)
   */
  async getTodayTodosWithCompleted(folderUrls?: string[]): Promise<{
    active: TodoItem[]
    completed: TodoItem[]
  }> {
    await this.ensureReady()

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    // 동기화 폴더의 모든 Todo 조회
    let allTodos: TodoItem[]
    if (folderUrls && folderUrls.length > 0) {
      allTodos = []
      for (const folderUrl of folderUrls) {
        const folderTodos = await this.getTodosByFolder(folderUrl)
        allTodos.push(...folderTodos)
      }
    } else {
      allTodos = await this.getSyncFolderTodos()
    }

    const active: TodoItem[] = []
    const completed: TodoItem[] = []

    allTodos.forEach((todo) => {
      let isTodayTodo = false

      // dueDate가 오늘인지 확인
      if (todo.dueDate) {
        const dueDate = new Date(todo.dueDate)
        dueDate.setHours(0, 0, 0, 0)
        if (dueDate.getTime() === today.getTime()) {
          isTodayTodo = true
        }
      }

      // startDate가 오늘인지 확인 (dueDate가 오늘이 아닌 경우에만)
      if (!isTodayTodo && todo.startDate) {
        const startDate = new Date(todo.startDate)
        startDate.setHours(0, 0, 0, 0)
        if (startDate.getTime() === today.getTime()) {
          isTodayTodo = true
        }
      }

      if (isTodayTodo) {
        if (todo.status === 'COMPLETED') {
          completed.push(todo)
        } else {
          active.push(todo)
        }
      }
    })

    return { active, completed }
  }
}
